import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearPendingDocImageFile,
  hasPendingDocImageFile,
  retryDocImageUpload,
  startDocImageUpload,
} from '../utils/docImageUploadBridge';
import { createLocalImageBlock, pickDocImageFile } from '../utils/imageFiles';

const MIN_WIDTH_PCT = 15;
const MAX_WIDTH_PCT = 100;

const revokeBlobUrl = (url) => {
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
};

/**
 * ImageBlock — doc image with classic corner-pin resize handles.
 * Width is stored as props.widthPercent (15–100); height follows aspect ratio.
 * Drag the image body to move the block (same as the side handle).
 * Failed uploads keep a local preview and offer Retry / Remove.
 */
export const ImageBlock = ({
  block,
  onUpdate,
  onDelete,
  selectedBlockId,
  dragListeners,
  dragAttributes,
}) => {
  const {
    publicUrl,
    localPreviewUrl,
    fileName,
    uploadStatus,
    errorMessage,
    widthPercent,
  } = block.props || {};

  const src = publicUrl || localPreviewUrl;
  const isError = uploadStatus === 'error' && !src;
  const isUploading =
    uploadStatus === 'uploading' || (uploadStatus === 'local' && !!localPreviewUrl);
  const uploadFailed = uploadStatus === 'error' && !!src;
  const canRetry = hasPendingDocImageFile(block.id);
  const selected = selectedBlockId === block.id;

  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const draftWidthRef = useRef(null);
  const [draftWidth, setDraftWidth] = useState(null);
  const [hovered, setHovered] = useState(false);

  // No widthPercent → show at intrinsic size (capped to column width).
  // Once the user resizes (or an explicit % is stored), use % of the column.
  const widthPct =
    draftWidth ?? (typeof widthPercent === 'number' ? widthPercent : null);
  const usePercentWidth = widthPct != null;

  const commitWidth = useCallback(
    (pct) => {
      const clamped = Math.round(Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, pct)));
      onUpdate?.(block.id, {
        props: { ...block.props, widthPercent: clamped },
      });
    },
    [block.id, block.props, onUpdate]
  );

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { side, containerLeft, containerWidth, frameRight } = drag;
      if (!containerWidth) return;

      let pct;
      if (side === 'right') {
        pct = ((e.clientX - containerLeft) / containerWidth) * 100;
      } else {
        pct = ((frameRight - e.clientX) / containerWidth) * 100;
      }
      const next = Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, pct));
      draftWidthRef.current = next;
      setDraftWidth(next);
    };

    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.classList.remove('doc-image-resizing');
      const current = draftWidthRef.current;
      draftWidthRef.current = null;
      setDraftWidth(null);
      if (current != null) commitWidth(current);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [commitWidth]);

  const startResize = (side) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const frame = frameRef.current;
    if (!frame) return;
    const fRect = frame.getBoundingClientRect();
    const wrapper = frame.closest('.block-wrapper') || frame.parentElement;
    const wRect = wrapper.getBoundingClientRect();
    if (!wRect.width) return;

    // Natural-size images have no stored % — seed from current rendered width
    const startPct =
      widthPct ??
      Math.min(
        MAX_WIDTH_PCT,
        Math.max(MIN_WIDTH_PCT, (fRect.width / wRect.width) * 100)
      );

    dragRef.current = {
      side,
      containerLeft: wRect.left,
      containerWidth: wRect.width,
      frameRight: fRect.right,
    };
    document.body.classList.add('doc-image-resizing');
    draftWidthRef.current = startPct;
    setDraftWidth(startPct);
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearPendingDocImageFile(block.id);
    revokeBlobUrl(localPreviewUrl);
    onDelete?.(block.id);
  };

  const handleRetry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!retryDocImageUpload(block.id)) return;
  };

  const handleChooseImage = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = await pickDocImageFile();
    if (!file) return;
    revokeBlobUrl(localPreviewUrl);
    clearPendingDocImageFile(block.id);
    const next = createLocalImageBlock(file);
    onUpdate?.(block.id, {
      type: 'image',
      content: '',
      props: { ...next.props, widthPercent },
      children: [],
    });
    if (next.props?.uploadStatus === 'local') {
      startDocImageUpload(block.id, file);
    }
  };

  const errorActions = (
    <div className="image-block__actions">
      {canRetry ? (
        <button
          type="button"
          className="image-block__action image-block__action--primary"
          onClick={handleRetry}
        >
          Retry
        </button>
      ) : (
        <button
          type="button"
          className="image-block__action image-block__action--primary"
          onClick={handleChooseImage}
        >
          Choose image
        </button>
      )}
      <button
        type="button"
        className="image-block__action"
        onClick={handleRemove}
      >
        Remove
      </button>
    </div>
  );

  if (isError) {
    return (
      <div className="image-block image-block--error" role="alert">
        <div className="image-block__error-title">Couldn’t add image</div>
        <div className="image-block__error-msg">
          {errorMessage || 'Something went wrong'}
        </div>
        {errorActions}
      </div>
    );
  }

  if (!src) {
    return (
      <div className="image-block image-block--empty">
        <div className="image-block__placeholder">No image</div>
        {errorActions}
      </div>
    );
  }

  const showHandles = selected || hovered || draftWidth != null;

  const openImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = publicUrl || src;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={[
        'image-block',
        isUploading ? 'image-block--uploading' : '',
        uploadFailed ? 'image-block--upload-failed' : '',
        selected ? 'image-block--selected' : '',
        draftWidth != null ? 'image-block--resizing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onContextMenu={openImage}
      title={publicUrl ? 'Right-click to open image' : undefined}
    >
      <div
        ref={frameRef}
        className={[
          'image-block__frame',
          usePercentWidth ? '' : 'image-block__frame--natural',
        ]
          .filter(Boolean)
          .join(' ')}
        style={usePercentWidth ? { width: `${widthPct}%` } : undefined}
        {...(dragListeners || {})}
        {...(dragAttributes || {})}
      >
        <img
          className="image-block__img"
          src={src}
          alt={fileName || 'Document image'}
          draggable={false}
        />
        {isUploading && (
          <div className="image-block__badge" title="Uploading to cloud storage">
            Uploading…
          </div>
        )}
        {uploadFailed && (
          <div
            className="image-block__fail-panel"
            role="alert"
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="image-block__fail-msg">Upload failed</div>
            {errorActions}
          </div>
        )}
        {showHandles && !uploadFailed && (
          <>
            <button
              type="button"
              className="image-block__pin image-block__pin--tl"
              aria-label="Resize from top-left"
              onPointerDown={startResize('left')}
            />
            <button
              type="button"
              className="image-block__pin image-block__pin--tr"
              aria-label="Resize from top-right"
              onPointerDown={startResize('right')}
            />
            <button
              type="button"
              className="image-block__pin image-block__pin--bl"
              aria-label="Resize from bottom-left"
              onPointerDown={startResize('left')}
            />
            <button
              type="button"
              className="image-block__pin image-block__pin--br"
              aria-label="Resize from bottom-right"
              onPointerDown={startResize('right')}
            />
          </>
        )}
      </div>
    </div>
  );
};
